import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { useProjects } from "../../api/queries";
import type { Project } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useFleet } from "../../fleet/FleetContext";
import { CreateProjectModal } from "./CreateProjectModal";

const COLUMNS: DataTableColumn<Project>[] = [
  {
    key: "name",
    header: "Name",
    cell: (project) => (
      // stopPropagation: the row itself is also a click target (DataTable's
      // onRowClick); without this, activating the link would navigate twice.
      <Link className="data-table__primary-link" href={`/projects/${project.id}`} onClick={(e) => e.stopPropagation()}>
        {project.name}
      </Link>
    ),
    className: "data-table__primary",
  },
  {
    key: "description",
    header: "Description",
    cell: (project) => project.description || "—",
    className: (project) => (project.description ? "data-table__secondary" : "data-table__empty-cell"),
  },
  {
    key: "createdBy",
    header: "Created by",
    cell: (project) => project.createdBy || "—",
    className: "data-table__secondary",
  },
  {
    key: "updatedAt",
    header: "Updated",
    cell: (project) => new Date(project.updatedAt).toLocaleString(),
    className: "data-table__secondary",
  },
];

/**
 * Hero flow, step 1: projects group the services a team deploys together.
 * Mirrors the real, merged backend contract (#63 projectsvc) exactly.
 */
export function ProjectsListPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch, isFetching } = useProjects(currentId, {
    cursor: paginator.cursor,
  });
  const [createOpen, setCreateOpen] = useState(false);

  // Mirrors the server's authz matrix (project:create is granted to
  // operator/admin only, server/internal/authz/model.go) — this only hides
  // the control; the server enforces the real decision on every request
  // regardless of what this check says (S-8).
  const canCreate = session?.role === "admin" || session?.role === "operator";

  const newProjectButton = (
    <Button variant="primary" onClick={() => setCreateOpen(true)}>
      New project
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Projects"
        description="Group the services your team builds and deploys together — the starting point for project → service → deploy."
        actions={canCreate ? newProjectButton : undefined}
      />

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading projects…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load projects. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No projects yet"
            description="Create one to start deploying services to this fleet."
            action={canCreate ? newProjectButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={COLUMNS}
              rows={data.items}
              rowKey={(project) => project.id}
              onRowClick={(project) => navigate(`/projects/${project.id}`)}
              ariaBusy={isFetching}
            />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="projects"
            />
          </>
        )}
      </QueryStates>

      {canCreate && (
        <CreateProjectModal open={createOpen} fleetId={currentId} onClose={() => setCreateOpen(false)} />
      )}
    </>
  );
}
